import { formatPhoneNumber } from '@/lib/crm/utils/phone';
import type { PhoneNumber } from '@/types/crm';

// Mirrors the backend's MAX_MULTI_VALUE_ITEMS cap
// (apps/crm_events/services/validators/property_value_validator.py). Must stay
// manually synced — the frontend cannot import the Python constant.
export const MAX_MULTI_VALUE_ITEMS = 100;

/**
 * Formats a value for display in announcements and tooltips.
 *
 * - Phone numbers: Formatted using E.164 or national format
 * - Objects: JSON stringified
 * - Primitives: String representation
 *
 * @param val - The value to format
 * @returns Formatted string representation
 */
export function formatValueForText(val: unknown): string {
  if (val === null || val === undefined) {
    return '';
  }
  // Handle phone numbers specially
  if (
    typeof val === 'object' &&
    val !== null &&
    'country_code' in val &&
    'national_number' in val
  ) {
    return formatPhoneNumber(val as PhoneNumber);
  }
  if (typeof val === 'object') {
    return JSON.stringify(val);
  }
  return String(val);
}

/**
 * Normalizes an object by sorting its keys before JSON stringification.
 * This ensures order-independent comparison.
 *
 * @param obj - The object to normalize
 * @returns Normalized JSON string with sorted keys
 */
function normalizeObject(obj: unknown): string {
  if (obj === null || obj === undefined) {
    return JSON.stringify(obj);
  }
  if (typeof obj !== 'object') {
    return JSON.stringify(obj);
  }

  // Sort object keys recursively
  const sorted = Object.keys(obj)
    .sort()
    .reduce(
      (acc, key) => {
        const value = (obj as Record<string, unknown>)[key];
        // Recursively normalize nested objects
        if (
          typeof value === 'object' &&
          value !== null &&
          !Array.isArray(value)
        ) {
          acc[key] = JSON.parse(normalizeObject(value));
        } else {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, unknown>
    );

  return JSON.stringify(sorted);
}

/**
 * Checks if a value already exists in the array.
 *
 * - Strings: Case-insensitive comparison
 * - Objects: Deep equality via normalized JSON comparison (order-independent)
 *
 * @param newVal - The new value to check
 * @param existingValues - The existing array of values
 * @returns true if the value is a duplicate
 */
export function isDuplicateValue(
  newVal: unknown,
  existingValues: unknown[]
): boolean {
  if (typeof newVal === 'string') {
    const newValStr = newVal.toLowerCase();
    return existingValues.some(
      v => typeof v === 'string' && v.toLowerCase() === newValStr
    );
  }
  // For non-strings, use normalized JSON comparison (order-independent)
  const newValJson = normalizeObject(newVal);
  return existingValues.some(v => normalizeObject(v) === newValJson);
}

/**
 * Validates a new value before adding to the multi-value array.
 *
 * Checks:
 * - Value is not empty (null, undefined, or empty string)
 * - Array has not reached max limit (MAX_MULTI_VALUE_ITEMS)
 * - Value is not a duplicate (case-insensitive for strings)
 *
 * @param value - The value to validate
 * @param valueArray - The existing array of values
 * @returns Validation result with error message if invalid
 */
export function validateNewValue(
  value: unknown,
  valueArray: unknown[]
): { isValid: boolean; error: string | null } {
  // Check if value is empty
  if (value === null || value === undefined) {
    return { isValid: false, error: null }; // Silent validation failure
  }
  if (typeof value === 'string' && value.trim() === '') {
    return { isValid: false, error: null }; // Silent validation failure
  }

  // Check max limit
  if (valueArray.length >= MAX_MULTI_VALUE_ITEMS) {
    return {
      isValid: false,
      error: `Maximum of ${MAX_MULTI_VALUE_ITEMS} values reached. Cannot add more.`,
    };
  }

  // Check duplicates
  if (isDuplicateValue(value, valueArray)) {
    return {
      isValid: false,
      error: 'This value already exists. Please enter a different value.',
    };
  }

  return { isValid: true, error: null };
}
